import { Table, Tag, Tooltip } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DAY_FORMAT, DAYTIME_FORMAT } from '../../constants';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';
import PartnerLogo from '../PartnerLogo';
import DriverAvatar from '../DriverAvatar';
import { tenantConfig } from '../../config/tenantConfig';

const MOBILE_HIDDEN_KEYS = ['orders', 'collected', 'partners', 'startTime', 'endTime', 'duration', 'clientService'];
const FIXED_KEYS = ['id', 'action'];

const formatLocalTime = (iso) => (iso ? moment(iso).format('HH:mm') : '-');
// OVO-130 — minutes → "HH:MM" for the shift / client-service columns.
const formatMinsHHMM = (mins) => {
  if (mins == null || mins <= 0) return '-';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export default function RoutesTable({
  withAction,
  dataSource,
  selectedRowKeys,
  onSelectChange,
  partnerId,
  configRef,
  onPageSizeChange,
  // OVO-002 — when rendered for a partner viewer, drop columns that would
  // expose other partners on the same (mutualized) route. The backend's
  // /partners/me/routes endpoint already omits these fields, so the
  // columns would render "-" anyway; hiding them keeps the layout clean
  // and prevents accidental leaks if the data shape ever changes.
  viewOnly = false,
  partnerDefaults,
  ...props
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { t } = useTranslation('routes');
  const { t: tc } = useTranslation('common');

  const allColumns = useMemo(() => {
    const cols = [
      {
        title: t('id'),
        dataIndex: 'id',
        key: 'id',
        fixed: 'left',
        width: 100,
        sorter: true,
        defaultSortOrder: 'descend',
        render: (value, record) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Link
              to={viewOnly ? `/partner/routes/${value}` : `/admin/routes/${value}`}
              onClick={(e) => e.stopPropagation()}
            >
              {value}
            </Link>
            {/* Mutualised route — shared with other partner(s). ADMIN-ONLY:
                OVO-002 forbids letting a partner viewer learn their route is
                shared, so the tag is gated on !viewOnly (and the backend strips
                `mutualized` from the partner-viewer endpoint entirely). We also
                never name the other partners — just flag that it's shared. */}
            {!viewOnly && record?.mutualized && (
              <Tooltip title={t('sharedRouteTooltip', { count: record.otherPartnerCount || 1 })}>
                <Tag color="gold" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px', padding: '0 5px' }}>
                  {t('shared')}
                </Tag>
              </Tooltip>
            )}
          </span>
        ),
      },
      {
        title: tc('date'),
        dataIndex: 'date',
        key: 'date',
        width: 200,
        className: 'border-right',
        sorter: true,
        render: (value) => (value ? moment(value).format(DAY_FORMAT) : ''),
      },
      {
        title: t('driver'),
        dataIndex: 'driverName',
        key: 'driverName',
        render: (_va, record) =>
          record?.driver?.name ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <DriverAvatar name={record.driver.name} photoKey={record.driver.photoFile?.key} size={22} />
              {/* Link to the driver page (admin only — partner viewers can't reach it). */}
              {!viewOnly && record.driver.id ? (
                <Link to={`/admin/drivers/${record.driver.id}`} onClick={(e) => e.stopPropagation()}>
                  {record.driver.name}
                </Link>
              ) : (
                record.driver.name
              )}
            </span>
          ) : (
            '-'
          ),
      },
      // Start / End Time. In a partner-scoped list the backend sends the
      // PARTNER's own first-pickup → last-delivery window (partnerStartTime /
      // partnerEndTime, HH:mm); we prefer those over the driver's whole-route
      // shift badge (which was showing the wrong end — the driver's badge-out).
      {
        title: t('startTime'),
        key: 'startTime',
        width: 100,
        render: (record) =>
          record?.partnerStartTime !== undefined
            ? record.partnerStartTime || '-'
            : formatLocalTime(record?.actualShiftStart),
      },
      {
        title: t('endTime'),
        key: 'endTime',
        width: 100,
        render: (record) =>
          record?.partnerEndTime !== undefined ? record.partnerEndTime || '-' : formatLocalTime(record?.actualShiftEnd),
      },
      // OVO-130 — shift duration (bike-out → bike-in). Uses the backend's
      // computed `durations.shiftMins` (actual badged boundaries, falling
      // back to the planned shift) instead of the raw actualShift* diff.
      {
        title: t('shiftDuration'),
        key: 'duration',
        width: 110,
        render: (record) => formatMinsHHMM(record?.durations?.shiftMins),
      },
      // OVO-130 — client service time (first pickup → last delivery), the
      // metric hourly clients are billed on.
      {
        title: t('clientService'),
        key: 'clientService',
        width: 120,
        render: (record) =>
          record?.partnerClientServiceMins !== undefined
            ? formatMinsHHMM(record.partnerClientServiceMins)
            : formatMinsHHMM(record?.durations?.clientServiceMins),
      },
      {
        title: t('stops'),
        key: 'stops',
        render: (record) => (record?.partnerStops !== undefined ? record.partnerStops : record?._count?.steps || 0),
      },
      {
        title: t('distance'),
        dataIndex: 'distance',
        key: 'distance',
        render: (value) => (value ? `${(value / 1000).toFixed(2)} km` : ''),
      },
      // OVO-130 — the optimizer-estimate duration columns (Travel Time /
      // Working Time / Service Time / Working %) were removed: they're
      // planning artifacts (and `workingMins` was the "1h61" bogus one),
      // superseded by the Shift Duration + Client Service columns above.
      // OVO-106 — CO2 column. Hidden by default for users with old saved
      // column configs; toggleable via the gear icon. Renders nothing when the
      // fleet has no co2 factor configured.
      {
        title: 'CO₂',
        key: 'co2',
        align: 'right',
        width: 100,
        render: (_, record) => (record?.co2?.kg != null ? `${record.co2.kg.toFixed(2)} kg` : '-'),
      },
      {
        title: t('orders'),
        key: 'orders',
        render: (record) => {
          const totalOrders = record?._count?.orders || 0;
          const partnerOrders = record?.partnerOrderCount;
          const hasPartnerContext = partnerOrders !== undefined && partnerOrders !== null;
          const displayCount = hasPartnerContext ? partnerOrders : totalOrders;

          if (displayCount === 0 && !hasPartnerContext) return '0';

          const orderUrl = partnerId
            ? `/admin/orders?current=1&routeId=${record.id}&partnerId=${partnerId}`
            : `/admin/orders?current=1&routeId=${record.id}`;
          const link = (
            <Link to={orderUrl} onClick={(e) => e.stopPropagation()}>
              {displayCount}
            </Link>
          );

          if (hasPartnerContext && totalOrders > partnerOrders) {
            return (
              <span>
                {link}
                {/*<span style={{ color: 'var(--text-muted)', fontSize: 12 }}> / {totalOrders}</span>*/}
              </span>
            );
          }

          return link;
        },
      },
      // OVO-002 — these two columns expose other partners on mutualized
      // routes (the partner-name tag and the per-partner collected-items
      // breakdown). Drop them entirely from the partner-viewer table.
      ...(viewOnly
        ? []
        : [
            {
              title: t('collected'),
              key: 'collected',
              width: 140,
              render: (record) => {
                const items = record.collectedItemsByPartner;
                if (!items || items.length === 0) return '-';
                return items.map((p) => (
                  <Tag key={p.code} icon={<InboxOutlined />} color="orange" style={{ marginBottom: 2 }}>
                    {p.code}: {p.count}
                  </Tag>
                ));
              },
            },
            {
              title: t('partners'),
              key: 'partners',
              width: 180,
              render: (record) => {
                const partners = record?.partners;
                if (!partners || partners.length === 0) return '-';
                return partners.map((p) => (
                  // Partner CODE (full name on hover) — names made the tag
                  // column unreadably noisy on mutualized routes.
                  <Tooltip key={p.id} title={p.name}>
                    <Tag color="blue" style={{ marginBottom: 2, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <PartnerLogo name={p.name} logoKey={p.logoFile?.key} size={16} />
                      {p.code || p.name}
                    </Tag>
                  </Tooltip>
                ));
              },
            },
          ]),
      {
        title: tc('status'),
        dataIndex: 'status',
        key: 'status',
        width: 150,
        render: (value, record) => {
          let label;
          if (value === 'in_progress') {
            label = tc('orderStatuses.IN_PROGRESS');
          } else if (value === 'completed') {
            label = tc('orderStatuses.COMPLETED');
          } else if (value === 'dispatched') {
            const actionDisplay = record.actionStatus
              ? tc(`actionRouteStatuses.${record.actionStatus.toUpperCase()}`) || record.actionStatus
              : '';
            label = actionDisplay || tc('orderStatuses.DISPATCHED');
          } else {
            label = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';
          }
          return <span className={'status order ' + (value || '').toLowerCase()}>{label}</span>;
        },
      },
      {
        title: tc('updatedAt'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 250,
        render: (value) => (value ? moment(value).format(DAYTIME_FORMAT) : ''),
      },
    ];

    if (withAction) {
      cols.push({
        title: '',
        key: 'action',
        width: 120,
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
        className: 'border-left',
      });
    }

    // OVO-132 — drop the per-partner column in a single-tenant instance.
    return tenantConfig.isSingleTenant ? cols.filter((c) => c.key !== 'partners') : cols;
  }, [withAction, partnerId, viewOnly]);

  const columnConfig = useColumnConfig('routes-columns', allColumns, FIXED_KEYS, partnerDefaults);

  // Expose openDrawer to parent via ref
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const displayColumns = isMobile
    ? columnConfig.visibleColumns.filter((col) => !MOBILE_HIDDEN_KEYS.includes(col.key))
    : columnConfig.visibleColumns;

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="RoutesTable table-common">
      <Table
        columns={displayColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={(size) => {
          columnConfig.setPageSize(size);
          onPageSizeChange?.(size);
        }}
      />
    </div>
  );
}

// Export the config opener for use in page headers
export { default as useColumnConfig } from '../../hooks/useColumnConfig';
